import React, { useEffect, useRef, useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { managePublishingCredentials } from '../utils/publishingCredentialsClient';

const FIELDS = [
    { name: 'NFT_MINT_AUTHORITY_SECRET', label: 'Mint authority', help: '64-byte Solana keypair in JSON or base58. Existing collections must retain their delegated authority.' },
    { name: 'NFT_ARCHIVE_SOLANA_SECRET', label: 'Arweave archive wallet', help: 'Dedicated Solana keypair used for Turbo uploads. Its Turbo credit balance is managed separately.', provider: true },
    { name: 'NFT_PINATA_JWT', label: 'Pinata access token', help: 'Pinata JWT with file-upload and pin-list permissions. Replacement tokens should belong to the same Pinata account.', provider: true },
];
const STATE_LABELS = { missing: 'Not configured', configured: 'Configured', disabled: 'Disabled', invalid: 'Needs correction' };
export default function PublishingCredentials() {
    const wallet = useWallet();
    const walletRef = useRef(wallet);
    walletRef.current = wallet;
    const active = useRef(true);
    const busyRef = useRef(false);
    const [credentials, setCredentials] = useState(null);
    const [values, setValues] = useState({});
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    useEffect(() => {
        active.current = true;
        return () => { active.current = false; };
    }, []);
    const run = async (action, field) => {
        if (busyRef.current) return;
        const publicKey = wallet.publicKey?.toBase58?.();
        const isCurrent = () => active.current && walletRef.current.connected &&
            walletRef.current.publicKey?.toBase58?.() === publicKey;
        busyRef.current = true;
        setBusy(true); setError(''); setMessage('');
        try {
            const current = credentials?.find(item => item.name === field?.name);
            const result = await managePublishingCredentials(wallet, action === 'status' ? 'status' : 'replace',
                action === 'status' ? {} : { name: field.name, value: action === 'disable' ? 'DISABLED' : values[field.name],
                    expectedVersion: current?.version || '' }, { isCurrent });
            if (!isCurrent()) return;
            if (action === 'status') setCredentials(result.credentials);
            else {
                setCredentials(previous => previous.map(item => item.name === field.name ? result.credential : item));
                setMessage(field.label + ' saved.');
            }
        } catch (failure) {
            if (isCurrent()) setError(failure.message || 'Unable to manage publishing credentials.');
        } finally {
            busyRef.current = false;
            if (isCurrent()) { setBusy(false); if (field) setValues(previous => ({ ...previous, [field.name]: '' })); }
        }
    };
    return <section className="admin-panel ph-no-capture" data-ph-no-capture="true" style={{ padding: 16, margin: '12px 0' }}>
        <h2>Publishing credentials</h2>
        <p>View configuration status and public wallet addresses. Saved private keys and tokens are never displayed.
            Each change requires a fresh wallet authorization. Configured status does not verify provider access or upload funding.</p>
        <button type="button" className="muulti-button" disabled={busy || !wallet.connected} onClick={() => run('status')}>
            {busy ? 'Working...' : credentials ? 'Refresh credential status' : 'Load credential status'}
        </button>
        {credentials && FIELDS.map(field => {
            const credential = credentials.find(item => item.name === field.name);
            return <form key={field.name} aria-label={field.label} style={{ marginTop: 20 }}
                onSubmit={event => { event.preventDefault(); run('replace', field); }}>
                <h3>{field.label}</h3>
                <p>{STATE_LABELS[credential?.state] || 'Unknown status'}
                    {credential?.version ? ' - Version ' + credential.version : ''}</p>
                {credential?.publicKey && <p>Public wallet: <code style={{ overflowWrap: 'anywhere' }}>{credential.publicKey}</code></p>}
                {credential?.updatedAt && <p>Last updated: {new Date(credential.updatedAt).toLocaleString()}</p>}
                <p>{field.help}</p>
                <label htmlFor={field.name}>New {field.label.toLowerCase()}
                    <input id={field.name} className="muulti-input ph-no-capture" type="password"
                        autoComplete="new-password" spellCheck={false} maxLength={16384}
                        value={values[field.name] || ''} disabled={busy}
                        onChange={event => setValues(previous => ({ ...previous, [field.name]: event.target.value }))} />
                </label>
                <button type="submit" className="muulti-button" disabled={busy || !values[field.name]?.trim()}>
                    Save {field.label.toLowerCase()}
                </button>
                {field.provider && <button type="button" className="muulti-button" disabled={busy || credential?.state === 'disabled'}
                    onClick={() => run('disable', field)}>Disable {field.label.toLowerCase()}</button>}
            </form>;
        })}
        {error && <p role="alert">{error}</p>}
        {message && <p role="status">{message}</p>}
    </section>;
}
